import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { InsightsBreakdown } from '@/api/assistantInsights/assistantInsightsApi.types';
import type { IconName } from '@/components/common/Icon';
import { ChartCard } from '@/components/common/charts/ChartCard';
import {
  CHART_AXIS_TICK,
  CHART_TOOLTIP_CONTENT_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
} from '@/components/common/charts/chartStyles';
import { CHART_ROLE_COLORS } from '@/theme/chartPalette';
import { formatAxisDollars, formatMicroUsd, microUsdToDollars } from './usageFormatting';

const BAR_ROW_HEIGHT = 36;
const MINIMUM_CHART_HEIGHT = 160;
const DIMMED_OPACITY = 0.35;
const CATEGORY_AXIS_WIDTH = 150;
const MICRO_UNITS_PER_DOLLAR = 1_000_000;

interface UsageBreakdownChartProps {
  title: string;
  hint: string;
  iconName: IconName;
  items: InsightsBreakdown[];
  selectedKey: string | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  labelFor: (key: string) => string;
  colorFor: (key: string) => string;
  onSelect: (key: string | null) => void;
}

interface BreakdownRow {
  key: string;
  label: string;
  dollars: number;
  questions: number;
}

export function UsageBreakdownChart({
  title,
  hint,
  iconName,
  items,
  selectedKey,
  isLoading,
  isError,
  onRetry,
  labelFor,
  colorFor,
  onSelect,
}: UsageBreakdownChartProps) {
  const { t, i18n } = useTranslation('assistant');
  const rows: BreakdownRow[] = items.map(function toRow(item) {
    return {
      key: item.key,
      label: labelFor(item.key),
      dollars: microUsdToDollars(item.costMicroUsd),
      questions: item.questions,
    };
  });
  const height = Math.max(MINIMUM_CHART_HEIGHT, rows.length * BAR_ROW_HEIGHT + 24);

  function formatDollars(value: number): string {
    return formatMicroUsd(value * MICRO_UNITS_PER_DOLLAR, i18n.language);
  }

  return (
    <ChartCard
      title={title}
      iconName={iconName}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      isEmpty={rows.length === 0}
      emptyLabel={t('insights.trend.empty')}
    >
      <div role="img" aria-label={title} className="w-full">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 4, right: 16, bottom: 0, left: 4 }}
          >
            <CartesianGrid
              stroke={CHART_ROLE_COLORS.grid}
              strokeDasharray="3 3"
              horizontal={false}
            />
            <XAxis
              type="number"
              tick={CHART_AXIS_TICK}
              tickFormatter={function formatTick(value: number) {
                return formatAxisDollars(value, i18n.language);
              }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={CHART_AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={CATEGORY_AXIS_WIDTH}
            />
            <Tooltip
              cursor={{ fill: CHART_ROLE_COLORS.grid, opacity: 0.4 }}
              contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL_STYLE}
              itemStyle={CHART_TOOLTIP_ITEM_STYLE}
              formatter={function formatTooltip(value, _name, entry) {
                const payload = entry.payload as BreakdownRow | undefined;
                const questions = payload === undefined ? 0 : payload.questions;
                return [
                  formatDollars(Number(value)) +
                    ' · ' +
                    t('insights.tooltip.questions', { count: questions }),
                  t('insights.kpis.cost'),
                ];
              }}
            />
            <Bar
              dataKey="dollars"
              radius={[0, 6, 6, 0]}
              cursor="pointer"
              onClick={function toggleSelection(_entry, index) {
                const row = rows[index];
                if (row !== undefined) {
                  onSelect(row.key === selectedKey ? null : row.key);
                }
              }}
            >
              {rows.map(function renderCell(row) {
                const isDimmed = selectedKey !== null && row.key !== selectedKey;
                return (
                  <Cell
                    key={row.key}
                    fill={colorFor(row.key)}
                    fillOpacity={isDimmed ? DIMMED_OPACITY : 1}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-zoomuted mt-2">{hint}</p>
    </ChartCard>
  );
}
